import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Globe } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/context/I18nContext";

export default function Contact() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error(t.contact.toastFill);
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.toastSuccess);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.contact.toastError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-background" data-testid="contact-section">
      <div className="container-page section-y">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <span className="overline">{t.contact.eyebrow}</span>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1]">
              {t.contact.heading1} <br />
              <span className="text-primary">{t.contact.heading1Accent}</span>
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              {t.contact.description}
            </p>

            <div className="mt-10 space-y-5">
              <InfoRow icon={MapPin} title={t.contact.addressTitle}>
                {t.contact.addressLine1}<br />{t.contact.addressLine2}
              </InfoRow>
              <InfoRow icon={Phone} title={t.contact.phoneTitle}>
                <a href="tel:+966500338215" className="hover:text-primary transition-colors">+966 50 033 8215</a>
                <span className="mx-2 text-border">·</span>
                <a href="tel:+966507974383" className="hover:text-primary transition-colors">+966 50 797 4383</a>
              </InfoRow>
              <InfoRow icon={Mail} title={t.contact.emailTitle}>
                <a href="mailto:m.ali@mnh-ksa.com" className="hover:text-primary transition-colors">
                  m.ali@mnh-ksa.com
                </a>
              </InfoRow>
              <InfoRow icon={Globe} title={t.contact.websiteTitle}>
                <a href="https://www.mnh-ksa.com" target="_blank" rel="noreferrer" className="hover:text-primary transition-colors">
                  www.mnh-ksa.com
                </a>
              </InfoRow>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={submit}
            className="lg:col-span-7 bg-secondary text-secondary-foreground p-8 sm:p-12 grain"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t.contact.labelName}>
                <Input
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder={t.contact.placeholderName}
                  className="bg-transparent border-white/15 text-white placeholder:text-white/40 focus-visible:ring-primary rounded-sm h-12"
                  data-testid="contact-input-name"
                />
              </Field>
              <Field label={t.contact.labelEmail}>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder={t.contact.placeholderEmail}
                  className="bg-transparent border-white/15 text-white placeholder:text-white/40 focus-visible:ring-primary rounded-sm h-12"
                  data-testid="contact-input-email"
                />
              </Field>
              <Field label={t.contact.labelPhone}>
                <Input
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder={t.contact.placeholderPhone}
                  className="bg-transparent border-white/15 text-white placeholder:text-white/40 focus-visible:ring-primary rounded-sm h-12"
                  data-testid="contact-input-phone"
                />
              </Field>
              <Field label={t.contact.labelService}>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger
                    className="bg-transparent border-white/15 text-white rounded-sm h-12 data-[placeholder]:text-white/40"
                    data-testid="contact-select-service"
                  >
                    <SelectValue placeholder={t.contact.selectService} />
                  </SelectTrigger>
                  <SelectContent>
                    {t.contact.serviceOptions.map((s, i) => (
                      <SelectItem key={i} value={s} data-testid={`contact-service-option-${i}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="mt-5">
              <Field label={t.contact.labelMessage}>
                <Textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder={t.contact.placeholderMessage}
                  className="bg-transparent border-white/15 text-white placeholder:text-white/40 focus-visible:ring-primary rounded-sm"
                  data-testid="contact-input-message"
                />
              </Field>
            </div>
            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <p className="text-xs text-secondary-foreground/55 max-w-sm">{t.contact.disclaimer}</p>
              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8"
                data-testid="contact-submit-btn"
              >
                {loading ? t.contact.submitting : (
                  <>
                    {t.contact.submit} <Send className={`h-4 w-4 ${isRtl ? "mr-2 scale-x-[-1]" : "ml-2"}`} />
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="h-10 w-10 border border-border flex items-center justify-center bg-muted shrink-0">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-secondary-foreground/80 mb-2 block text-xs uppercase tracking-[0.2em]">{label}</Label>
      {children}
    </div>
  );
}
